/**
 * Turning what the user dropped into PAGES.
 *
 * The cleanup tool used to hold one picture. A phone photo of a five-page
 * contract is five pictures, and cleaning them one at a time — upload, tune,
 * download, repeat — is the actual work the tool was supposed to remove. So an
 * import is a list of pages, and a PDF counts as one page per page.
 *
 * A page holds a BLOB, not pixels: twenty 12-megapixel photos decoded at once is
 * a few hundred MB of ImageData and a dead tab on a phone. The blob is decoded
 * on demand (see useDocumentCleanupPages) and the thumbnail — made once, here —
 * is what the rail draws in the meantime.
 */

import * as pdfjsLib from 'pdfjs-dist';
import {
    MAX_FILE_BYTES,
    DEFAULT_ADJUSTMENTS,
    isSupportedDocumentFile,
} from './documentCleanupEngine';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

/** Enough for a whole exercise book; beyond this the rail stops being usable. */
export const MAX_PAGES = 40;

/** Long edge a PDF page is rasterised at — plenty for print, cheap to hold. */
const PDF_PAGE_MAX_DIM = 2400;

/** Long edge of a rail thumbnail. */
const THUMB_MAX_DIM = 220;

let seq = 0;
export const createPageId = () => `page-${Date.now().toString(36)}-${(seq += 1)}`;

export const isPdfFile = (file) => file?.type === 'application/pdf'
    || /\.pdf$/i.test(file?.name || '');

function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.92) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), type, quality);
    });
}

/** A crafted PDF can keep pdf.js busy for as long as it likes: opening it, and drawing each page, are each given this long. */
const PDF_STEP_TIMEOUT_MS = 20000;
/** The biggest single image (in pixels) pdf.js will draw — a hostile PDF can claim a gigapixel one. A 600 dpi A3 scan is ~50 million. */
const PDF_MAX_IMAGE_PIXELS = 80_000_000;

/** `promise`, or a rejection (after running `onTimeout`) when it has not settled within `ms`. */
function within(promise, ms, onTimeout) {
    let timer;
    const limit = new Promise((_, reject) => {
        timer = setTimeout(() => { onTimeout?.(); reject(new Error('timeout')); }, ms);
    });
    return Promise.race([promise, limit]).finally(() => clearTimeout(timer));
}

/**
 * Every page of a PDF as its own JPEG blob, rendered white-backed.
 *
 * These PDFs can come from strangers (customers send them to the Photo Booth), so: no eval, no XFA, a cap on the images it will draw,
 * a time limit on every step, and the loading task is always released — a stuck or hostile file must not leave the tab frozen or leak a worker.
 */
async function pdfToPageBlobs(file) {
    const data = new Uint8Array(await file.arrayBuffer());
    const task = pdfjsLib.getDocument({ data, isEvalSupported: false, enableXfa: false, maxImageSize: PDF_MAX_IMAGE_PIXELS });
    try {
        const pdf = await within(task.promise, PDF_STEP_TIMEOUT_MS, () => task.destroy());
        const base = (file.name || 'document').replace(/\.pdf$/i, '');
        const out = [];

        for (let n = 1; n <= pdf.numPages; n += 1) {
            const page = await within(pdf.getPage(n), PDF_STEP_TIMEOUT_MS);
            const raw = page.getViewport({ scale: 1 });
            // Drawn up to 3x (sharp for print) and NEVER past PDF_PAGE_MAX_DIM: a page that is huge on paper must not become a huge canvas
            // (a 14400 pt page used to be drawn 1:1 — 200 megapixels, ~800 MB, per page).
            const scale = Math.min(3, PDF_PAGE_MAX_DIM / Math.max(raw.width, raw.height));
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.floor(viewport.width));
            canvas.height = Math.max(1, Math.floor(viewport.height));
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('no canvas');
            // A PDF page is transparent where nothing is drawn; on a JPEG that turns
            // black, so the paper is painted first.
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            const render = page.render({ canvasContext: ctx, viewport, background: '#ffffff' });
            await within(render.promise, PDF_STEP_TIMEOUT_MS, () => render.cancel());
            out.push({ blob: await canvasToBlob(canvas), name: `${base}-${n}.jpg` });
            page.cleanup();
            canvas.width = 0; // let go of the bitmap now, not whenever the GC gets to it
            canvas.height = 0;
            if (out.length >= MAX_PAGES) break;
        }

        return out;
    } finally {
        Promise.resolve(task.destroy()).catch(() => {});
    }
}

/** A small JPEG data URL for the rail, from anything drawable. */
export async function makeThumbUrl(drawable, width, height) {
    const scale = Math.min(1, THUMB_MAX_DIM / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(drawable, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.7);
}

/**
 * A tile for the rail, made straight from the blob at import.
 *
 * Without this a page shows an empty tile until it is selected, and a rail of
 * grey boxes is no way to find page 14. Decoded through createImageBitmap and
 * released immediately, so a 40-page import never holds 40 full-size decodes.
 */
async function thumbFromBlob(blob) {
    try {
        const bitmap = await createImageBitmap(blob);
        const url = await makeThumbUrl(bitmap, bitmap.width, bitmap.height);
        bitmap.close?.();
        return url;
    } catch {
        // HEIC and friends can fail here while the full loader still copes.
        return null;
    }
}

export function newPage(blob, name) {
    return {
        id: createPageId(),
        name,
        blob,
        // Geometry and cleanup live per page — that is the whole point of the
        // rail. A page the user has not touched carries the defaults.
        rotation: 0,
        quad: null,
        preset: 'none',
        adjustments: { ...DEFAULT_ADJUSTMENTS },
        thumbUrl: null,
        width: 0,
        height: 0,
    };
}

/**
 * Files (and PDFs) → pages, with the reasons anything was skipped.
 *
 * Nothing here throws: one unreadable file out of twenty must not lose the other
 * nineteen, so failures come back as `errors` for the caller to report once.
 */
export async function pagesFromFiles(files, { room = MAX_PAGES } = {}) {
    const pages = [];
    const errors = [];
    let left = room;

    for (const file of Array.from(files || [])) {
        if (left <= 0) { errors.push({ file, reason: 'tooMany' }); continue; }
        if (file.size > MAX_FILE_BYTES) { errors.push({ file, reason: 'tooLarge' }); continue; }

        if (isPdfFile(file)) {
            try {
                const rendered = await pdfToPageBlobs(file);
                for (const { blob, name } of rendered) {
                    if (left <= 0) { errors.push({ file, reason: 'tooMany' }); break; }
                    pages.push(newPage(blob, name));
                    left -= 1;
                }
            } catch {
                errors.push({ file, reason: 'pdfFailed' });
            }
            continue;
        }

        if (!isSupportedDocumentFile(file)) { errors.push({ file, reason: 'invalidType' }); continue; }
        pages.push(newPage(file, file.name));
        left -= 1;
    }

    // Tiles last, in one pass: the list is what the caller is waiting on, and a
    // page with no tile yet still selects, crops and exports.
    for (const page of pages) {
        page.thumbUrl = await thumbFromBlob(page.blob);
    }

    return { pages, errors };
}
